// MANTENER LA PANTALLA DESPIERTA mientras se está dictando.
//
// POR QUÉ EXISTE. Dictando el periodontograma se está un buen rato hablando sin TOCAR la pantalla:
// "uno seis, tres dos tres, sangra…". Para el móvil o la tablet eso es un dispositivo ocioso, así que
// apaga la pantalla — y al apagarse **se corta el micrófono**. El doctor sigue dictando media arcada
// contra un micrófono muerto y solo se entera al parar, cuando ya ha perdido lo dicho.
//
// LO QUE HACE. Pide un Screen Wake Lock mientras `activo` sea true y lo suelta en cuanto deja de
// serlo. Es la API del navegador para justo esto (Chrome/Edge/Android y Safari 16.4+); no engaña al
// sistema con vídeos invisibles ni con reproducciones de silencio.
//
// LOS DOS DETALLES QUE HACEN QUE FUNCIONE DE VERDAD:
//   1. EL BLOQUEO SE PIERDE SOLO al minimizar, cambiar de pestaña o apagar la pantalla a mano — el
//      navegador lo suelta y NO lo devuelve. Por eso se vuelve a pedir en `visibilitychange`: sin
//      esto, quien atiende una llamada a media exploración vuelve y la pantalla se le apaga otra vez.
//   2. NUNCA LANZA. El permiso puede estar denegado, la pestaña puede no estar visible, o el
//      navegador puede no tener la API (iOS antiguo). Todo eso se traga: el dictado tiene que
//      funcionar igual, solo que sin este extra.
import { useEffect, useRef } from 'react'

export default function usePantallaDespierta(activo) {
  const lockRef = useRef(null)

  useEffect(() => {
    if (!activo) return
    if (typeof navigator === 'undefined' || !navigator.wakeLock?.request) return

    let vivo = true

    const pedir = async () => {
      // `request` falla si el documento no está visible: no es un error, es que ahora no toca.
      if (!vivo || document.visibilityState !== 'visible' || lockRef.current) return
      try {
        const lock = await navigator.wakeLock.request('screen')
        if (!vivo) { try { await lock.release() } catch (_) {} return }
        lockRef.current = lock
        // El propio navegador avisa cuando lo suelta: se limpia la referencia para poder volver a
        // pedirlo, en vez de creer que se sigue teniendo uno que ya no existe.
        lock.addEventListener('release', () => { if (lockRef.current === lock) lockRef.current = null })
      } catch (_) { /* sin permiso o no disponible: se sigue sin él */ }
    }

    const alVolver = () => { if (document.visibilityState === 'visible') pedir() }

    pedir()
    document.addEventListener('visibilitychange', alVolver)

    return () => {
      vivo = false
      document.removeEventListener('visibilitychange', alVolver)
      const lock = lockRef.current
      lockRef.current = null
      if (lock) { try { lock.release() } catch (_) {} }
    }
  }, [activo])
}
